// The shell's in-memory app-token store (feature 2026-09-18-audience-bound-dp-token,
// D-1, D-5, WS-2 §3.3).
//
// One module-level map from a `release.apps_catalog` uuid to the app token
// minted for it and the wall-clock instant it expires, beside `AppHost`'s
// `moduleCache`.
//
// ⛔ Nothing here writes `localStorage` or `sessionStorage`. The shell holds
// no second bearer in storage today and this design adds none: an app token
// that survives a tab reload would outlive the session-change clear below.

/** One stored app token: the bearer and the instant it stops being usable. */
interface StoredAppToken {
  readonly accessToken: string;
  readonly expiresAtMs: number;
}

/** Catalog uuid to the app token minted for it under the recorded session. */
const tokens = new Map<string, StoredAppToken>();

/** Catalog uuid to the mint in flight for it, so concurrent callers share one. */
const mintsInFlight = new Map<string, Promise<string | null>>();

/** The session token every entry in `tokens` was minted under. */
let sessionTokenOfMap: string | null = null;

/**
 * Which emptying of the map the store is on. {@link syncSessionToken} on a
 * change and {@link clearAppTokens} both bump it, {@link ensureAppToken}
 * captures it, and a mint whose captured value has moved writes nothing
 * (R-055).
 *
 * ⛔ Emptying the two maps is not enough on its own. A `mint` already running
 * resolves after the clear and its `.then` still holds the
 * {@link putAppToken} call, so the previous session's token lands in the map
 * after the logout that was meant to drop it (ARCH-C2-1, SEC-C2-7).
 */
let generation = 0;

/** Empty both maps and move the generation, so no running mint can write. */
function emptyStore(): void {
  tokens.clear();
  mintsInFlight.clear();
  generation += 1;
}

/**
 * Record which session token the map belongs to, emptying it first when that
 * changes.
 *
 * @param sessionToken The session access token the shell now holds, or `null`
 *   after a logout.
 * @returns Nothing. It starts no request and no timer.
 *
 * When the argument differs from the recorded one, including on `null`, both
 * maps are emptied and the generation moves before the new value is recorded.
 * Logout and every refresh therefore drop every app token and every mint in
 * flight, and a token minted under one session can never be read under
 * another (security-audit.md C2 hop 5). Called with the value already
 * recorded, it does nothing.
 */
export function syncSessionToken(sessionToken: string | null): void {
  if (sessionToken === sessionTokenOfMap) return;
  emptyStore();
  sessionTokenOfMap = sessionToken;
}

/**
 * The stored app token for one app, or `null`.
 *
 * @param appUuid The app's `release.apps_catalog` id.
 * @returns The stored token, or `null` when there is none and when
 *   `Date.now()` is at or past the entry's expiry. An expired entry is
 *   removed on that read, so a later read cannot resurrect it.
 *
 * `null` is the fail-closed answer: the per-app fetch attaches no bearer and
 * the app's own request 401s, which is what re-mints.
 */
export function getAppToken(appUuid: string): string | null {
  const stored = tokens.get(appUuid);
  if (stored === undefined) return null;
  if (Date.now() >= stored.expiresAtMs) {
    tokens.delete(appUuid);
    return null;
  }
  return stored.accessToken;
}

/**
 * The mint in flight for one app, or `null` (#3291).
 *
 * @param appUuid The app's `release.apps_catalog` id.
 * @returns The promise {@link ensureAppToken} registered for that uuid and
 *   still holds, so a caller that awaits it gets the same answer as the mint's
 *   own caller: the token, or `null`. It never rejects. `null` when no mint for
 *   that uuid is in flight: none started, it settled, or a session change or
 *   {@link clearAppTokens} dropped it.
 *
 * Read-only. It starts no mint, reads no clock and writes nothing, and it reads
 * the entry of `appUuid` alone, so one app never sees another app's mint.
 * `createAppFetch`'s app arm awaits it before a send that has no stored token.
 */
export function pendingAppToken(appUuid: string): Promise<string | null> | null {
  return mintsInFlight.get(appUuid) ?? null;
}

/**
 * Drop the stored app token for one app, keeping every other entry.
 *
 * @param appUuid The app's `release.apps_catalog` id.
 * @returns Nothing. An entry that is not there is not an error.
 *
 * The app arm's `renew` in `client.ts` calls it before {@link ensureAppToken},
 * so a 401 that a live entry caused re-mints instead of replaying the bearer
 * that just 401'd (R-055, SEC-C2-3). It is the same delete {@link getAppToken}
 * performs on an expired entry, driven by the server's verdict rather than by
 * the clock.
 */
export function evictAppToken(appUuid: string): void {
  tokens.delete(appUuid);
}

/**
 * Store a minted app token against its lifetime.
 *
 * @param appUuid The app's `release.apps_catalog` id.
 * @param accessToken The compact access token from `AppTokenResponse.accessToken`.
 * @param expiresInSec `AppTokenResponse.expiresIn`, a positive integer of
 *   seconds, at most `ACCESS_TOKEN_TTL` seconds.
 * @returns Nothing.
 *
 * The entry expires at `Date.now() + expiresInSec * 1000`. An `expiresInSec`
 * of zero or less stores nothing, so a malformed lifetime cannot pin a token
 * in the map forever.
 */
export function putAppToken(appUuid: string, accessToken: string, expiresInSec: number): void {
  if (expiresInSec <= 0) return;
  tokens.set(appUuid, { accessToken, expiresAtMs: Date.now() + expiresInSec * 1000 });
}

/** What a mint hands the store: the bearer and the lifetime the server set. */
export interface MintedAppToken {
  /** The compact access token, `AppTokenResponse.accessToken`. */
  readonly accessToken: string;
  /** `AppTokenResponse.expiresIn`, the server's own lifetime in seconds. */
  readonly expiresInSec: number;
}

/**
 * The live app token for one app, minting it once if there is none.
 *
 * @param appUuid The app's `release.apps_catalog` id.
 * @param mint Mints a fresh token for that app and resolves it with the
 *   lifetime the exchange returned, or resolves `null` when it cannot. It is
 *   called at most once per uuid per in-flight window, and it is the only way
 *   a token enters the map from the network.
 *
 *   ⛔ It resolves the lifetime, not the bearer alone (R-041). {@link putAppToken}
 *   needs `expiresInSec`, and the only source of truth for it is
 *   `AppTokenResponse.expiresIn`, which the server caps at the session
 *   token's remaining lifetime. A caller that dropped the field would leave
 *   the store inventing an expiry the server never agreed to.
 * @returns The stored live token without calling `mint`. Otherwise the
 *   `accessToken` of one `mint`, stored through {@link putAppToken} against
 *   its `expiresInSec` and returned. A second call for the same uuid while
 *   one is in flight gets the SAME promise, mirroring `doRefresh`'s
 *   `refreshInFlight`, and the in-flight entry clears in a `finally`.
 *
 *   It resolves `null` instead when the store was emptied while the mint ran.
 *   The call captures the generation it started under, and the `.then` stores
 *   and returns the token only while that value still matches (R-055). So a
 *   logout or a session change during the exchange's round trip drops the
 *   token the exchange was still minting, and the caller holding that promise
 *   is told there is none.
 *
 * A rejected `mint` resolves `null` and stores nothing, so a failed exchange
 * leaves no stale bearer and rejects into no render.
 */
export function ensureAppToken(
  appUuid: string,
  mint: () => Promise<MintedAppToken | null>,
): Promise<string | null> {
  const stored = getAppToken(appUuid);
  if (stored !== null) return Promise.resolve(stored);

  const pending = mintsInFlight.get(appUuid);
  if (pending !== undefined) return pending;

  const startedAt = generation;
  const minting = mint()
    .then((minted) => {
      if (minted === null) return null;
      if (generation !== startedAt) return null;
      putAppToken(appUuid, minted.accessToken, minted.expiresInSec);
      return minted.accessToken;
    })
    .catch(() => null)
    .finally(() => {
      // Only if this mint is still the registered one. A clear drops the
      // entry and a later caller registers its own; an unconditional delete
      // here would evict that newer mint and let a third caller start a
      // second exchange for the same app.
      if (mintsInFlight.get(appUuid) === minting) mintsInFlight.delete(appUuid);
    });
  mintsInFlight.set(appUuid, minting);
  return minting;
}

/**
 * Empty both maps, move the generation, and forget the recorded session
 * token.
 *
 * @returns Nothing.
 *
 * `AppHost` calls it on unmount, so a logout that unmounts the host clears
 * the store like one that only changes `token`. A mint still in flight writes
 * nothing when it resolves, and the next caller for that app runs its own
 * mint (R-055).
 */
export function clearAppTokens(): void {
  emptyStore();
  sessionTokenOfMap = null;
}
